import { SESSIONS_PER_PAGE } from ':src/globalConstants';
import { useQueryParams } from '@motivo/guanyin/src/hooks/useQueryParams';
import {
  buildDateColumn,
  buildDurationColumn,
  buildSupervisorColumn,
  buildTypeColumn,
  EmptyOrLoadingState,
  SessionNotesModal,
  SuperviseeAttendeeList,
} from '@motivo/guanyin/src/components';
import { Columns, MoDataTable } from '@motivo/guanyin/src/components/MoDataTable';
import { DescriptionOutlined } from '@mui/icons-material';
import { Box, IconButton, Pagination, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { ResultOf } from ':src/graphql';
import { SessionDispositionEnum } from '@motivo/guanyin/src/lookup';
import useSupervisionModel from ':src/hooks/useSupervisionModel';
import { SessionDateFilter } from '../../utils';
import { sessionsQuery } from '../queries';

type SessionsQueryQuery = ResultOf<typeof sessionsQuery>;

type SessionAttendee = NonNullable<
  SessionsQueryQuery['sessions']
>['result'][number]['sessionAttendees'][number];

type SuperviseeNamesCellProps = {
  attendees: SessionAttendee[];
  showAttendance: boolean;
};

function SuperviseeNamesCell({ attendees, showAttendance }: SuperviseeNamesCellProps) {
  const superviseeUserIdsInOrganization = new Set(
    attendees
      .filter((attendee) => attendee.user.organizationUser)
      .map((attendee) => attendee.user.id),
  );

  return (
    <Box maxWidth="250px" minWidth="100px">
      <SuperviseeAttendeeList
        attendees={attendees}
        showAttendance={showAttendance}
        getProfilePath={(superviseeUserId) =>
          superviseeUserIdsInOrganization.has(superviseeUserId)
            ? `/supervisees/${superviseeUserId}`
            : undefined
        }
      />
    </Box>
  );
}

type SessionsTableProps = {
  userId?: number;
  hasSelectedSupervisor: boolean;
  userQueryLoading: boolean;
  sessionData: NonNullable<SessionsQueryQuery['sessions']>;
  sessionLoading: boolean;
  shouldDisplaySessionNotes: boolean;
};

type SessionRow = NonNullable<SessionsQueryQuery['sessions']>['result'][number];

/**
 * Check if this session is shown due to attendee-level cancellation
 * (not a session-level cancellation, but either all attendees or the filtered user cancelled with notice)
 */
const isAttendeeLevelCancellation = (session: SessionRow, filteredUserId: number | undefined) => {
  const isSessionLevelCancellation =
    session.sessionDispositionId === SessionDispositionEnum.CANCELLED;
  if (isSessionLevelCancellation) return false;

  if (filteredUserId) {
    const filteredAttendee = session.sessionAttendees.find(
      (attendee) => attendee.user.id === filteredUserId,
    );
    return filteredAttendee?.billable === false;
  } else {
    return session.sessionAttendees.every((attendee) => attendee.billable === false);
  }
};

export default function SessionsTable({
  userId,
  hasSelectedSupervisor,
  userQueryLoading,
  sessionData,
  sessionLoading,
  shouldDisplaySessionNotes,
}: SessionsTableProps) {
  let sessions = sessionData.result;
  const { total } = sessionData;
  const { isExternalAndInternal } = useSupervisionModel();
  const { queryParams, updateQueryParams } = useQueryParams();
  const { page: pageParam, sessionDateFilter } = queryParams;
  const isMissedTab = sessionDateFilter === SessionDateFilter.MISSED;
  const isCancelledTab = sessionDateFilter === SessionDateFilter.CANCELLED;
  const isTodayTab = sessionDateFilter === SessionDateFilter.TODAY_AND_PAST;
  const page = Number(pageParam) || 1;
  const totalPages = Math.ceil(total / SESSIONS_PER_PAGE);
  const showAttendance = isTodayTab || isMissedTab;
  const [notesSession, setNotesSession] = useState<SessionRow | null>(null);

  if (sessionLoading || userQueryLoading) {
    return (
      <Box py={4}>
        <EmptyOrLoadingState loading />
      </Box>
    );
  }

  const columns: Columns<SessionRow> = [buildDateColumn<SessionRow>()];

  if (!hasSelectedSupervisor) {
    columns.push(
      buildSupervisorColumn<SessionRow>({
        showBadge: isExternalAndInternal,
        routeConfig: { supervisorProfilePath: (id) => `/supervisors/${id}` },
        getIsInternal: (session) => session.organizationSession?.internal ?? false,
      }),
    );
  }

  columns.push(buildDurationColumn<SessionRow>(), buildTypeColumn<SessionRow>());

  if (isCancelledTab) {
    columns.push(
      {
        header: 'Cancelled on',
        content(session) {
          // For attendee-level cancellations, there's no session-level cancellation date
          if (isAttendeeLevelCancellation(session, userId)) {
            return <Typography>N/A</Typography>;
          }
          return session.cancelledAt
            ? DateTime.fromISO(session.cancelledAt).toLocaleString(DateTime.DATE_SHORT)
            : null;
        },
      },
      {
        header: 'Cancelled by',
        content(session) {
          if (isAttendeeLevelCancellation(session, userId)) {
            return <Typography>N/A</Typography>;
          }

          return (
            <Typography>
              {session.cancelledBy?.fullName}
              {session.unavailableParty
                ? ` (unavailable: ${session.unavailableParty.fullName})`
                : ''}
            </Typography>
          );
        },
      },
      {
        header: 'Reason',
        content(session) {
          if (isAttendeeLevelCancellation(session, userId)) {
            if (userId) {
              return <Typography>Supervisee cancelled with notice</Typography>;
            } else {
              return <Typography>All supervisees cancelled with notice</Typography>;
            }
          }

          return (
            <Typography whiteSpace="pre-line">
              {session.sessionCancellationReason?.name &&
                session.sessionCancellationReason.name !== 'Other' &&
                `${session.sessionCancellationReason.name}`}
              {session.cancellationReason ? `${session.cancellationReason}` : ''}
            </Typography>
          );
        },
      },
    );
  }

  columns.push({
    header: 'Supervisee(s)',
    content(session) {
      const attendees = isMissedTab
        ? session.sessionAttendees.filter((attendee) => attendee.billable && !attendee.attended)
        : session.sessionAttendees;

      return <SuperviseeNamesCell attendees={attendees} showAttendance={showAttendance} />;
    },
  });

  const hasAnyNotes = sessions.some(
    (session) => session.sessionNotes.length > 0 || !!session.sessionNotesInfo,
  );

  if (shouldDisplaySessionNotes && hasAnyNotes) {
    columns.push({
      header: '',
      content(session) {
        if (session.sessionNotes.length === 0 && !session.sessionNotesInfo) {
          return null;
        }

        return (
          <IconButton
            aria-label="View notes"
            onClick={() => setNotesSession(session)}
            sx={{ color: 'primary.main' }}
          >
            <DescriptionOutlined fontSize="small" />
          </IconButton>
        );
      },
    });
  }

  return (
    <>
      {sessions.length === 0 ? (
        <EmptyOrLoadingState title="No sessions found." />
      ) : (
        <MoDataTable columns={columns} data={sessions} />
      )}
      {totalPages > 1 && (
        <Box mt={2} display="flex" justifyContent="center">
          <Pagination
            page={page}
            count={totalPages}
            onChange={(_, page) => {
              window.scrollTo(0, 0);
              updateQueryParams({ page: page.toString() }, true);
            }}
          />
        </Box>
      )}

      <SessionNotesModal
        open={notesSession != null}
        onClose={() => setNotesSession(null)}
        legacyNotes={notesSession?.sessionNotes ?? null}
        sessionNotesInfo={notesSession?.sessionNotesInfo}
      />
    </>
  );
}
